import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { trackEvent } from '../lib/analytics';
import { useMixpanel } from '../lib/mixpanel';
import { useSSEStream } from './useSSEStream';

export type EnrichmentStageName =
  | 'profile'
  | 'posts'
  | 'reels'
  | 'comments'
  | 'analysis'
  | 'company'
  | 'company_posts';

export type EnrichmentStageStatus = 'success' | 'failed' | 'skipped' | 'not_reached';

export type EnrichmentStageReasonCode =
  | 'provider_unavailable'
  | 'auth_required'
  | 'profile_not_found'
  | 'unknown';

export interface EnrichmentStageReport {
  stage: EnrichmentStageName;
  status: EnrichmentStageStatus;
  reasonCode?: EnrichmentStageReasonCode;
}

export interface EnrichmentStatusEvent {
  status:
    | 'scraping'
    | 'scraping_comments'
    | 'analyzing'
    | 'persisting'
    | 'completed'
    | 'partial_success'
    | 'insufficient_data'
    | 'failed'
    | 'auth_failed';
  timestamp: string;
  profileUrl?: string;
  leadId?: string;
  score?: number;
  name?: string;
  error?: string;
  errorCode?: string;
  errorType?: string;
  skipped?: boolean;
  signalGap?: 'private_profile' | 'no_public_content';
  stages?: EnrichmentStageReport[];
}

const LEAD_PERSISTED_STATUSES: ReadonlySet<EnrichmentStatusEvent['status']> = new Set([
  'completed',
  'partial_success',
  'insufficient_data',
]);

interface UseEnrichmentStreamOptions {
  organizationId: string;
  enabled?: boolean;
  onEvent?: (event: EnrichmentStatusEvent) => void;
  getAccessToken?: () => Promise<string | null>;
}

interface UseEnrichmentStreamReturn {
  isConnected: boolean;
  lastEvent: EnrichmentStatusEvent | null;
}

export function useEnrichmentStream({
  organizationId,
  enabled = false,
  onEvent,
  getAccessToken,
}: UseEnrichmentStreamOptions): UseEnrichmentStreamReturn {
  const queryClient = useQueryClient();
  const mp = useMixpanel();
  const [lastEvent, setLastEvent] = useState<EnrichmentStatusEvent | null>(null);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  const handleMessage = useCallback(
    (data: string) => {
      try {
        const parsed: EnrichmentStatusEvent = JSON.parse(data);
        setLastEvent(parsed);
        onEventRef.current?.(parsed);

        if (LEAD_PERSISTED_STATUSES.has(parsed.status)) {
          trackEvent(mp, 'workflow.execution_completed', {
            workflow_type: 'enrichment',
            leads_created: parsed.leadId ? 1 : 0,
          });
          queryClient.invalidateQueries({ queryKey: ['workflow-executions'] });
          queryClient.invalidateQueries({ queryKey: ['leads'] });
          queryClient.invalidateQueries({ queryKey: ['lead'] });
        } else if (parsed.status === 'failed' || parsed.status === 'auth_failed') {
          trackEvent(mp, 'workflow.execution_failed', {
            workflow_type: 'enrichment',
            error_message: parsed.error,
            failure_reason: parsed.status,
          });
          queryClient.invalidateQueries({ queryKey: ['workflow-executions'] });
          queryClient.invalidateQueries({ queryKey: ['leads'] });
          queryClient.invalidateQueries({ queryKey: ['lead'] });
        }
      } catch {
        // skip malformed events
      }
    },
    [queryClient, mp],
  );

  const { isConnected } = useSSEStream({
    path: 'lead-enrichment/status/stream',
    organizationId,
    enabled,
    getAccessToken,
    onMessage: handleMessage,
    reconnect: { maxAttempts: 5, maxDelay: 15_000 },
  });

  return { isConnected, lastEvent };
}
